/**
 * Trade Navigation Utilities
 * Helper functions for navigating to market pages from trades
 */

import { getMarketByMarketIndex } from '../config/markets';

/**
 * Get market route path from a trade object
 * @param {Object} trade - Trade object with market_index
 * @returns {string|null} Route path like "/perps/markets/crypto/btc" or null if market not found
 */
export const getMarketRouteFromTrade = (trade) => {
  if (!trade || !trade.market_index) {
    return null;
  }

  const market = getMarketByMarketIndex(trade.market_index);
  if (!market) {
    console.warn('Market not found for trade:', trade.market_index);
    return null;
  }

  // Watch markets use /watch/ route with URL-safe IDs
  if (market.watchBrandKey || market.category === 'watches') {
    const urlId = market.id.replace(/-/g, '');
    return `/perps/markets/watch/${urlId}`;
  }

  return `/perps/markets/${market.category}/${market.id}`;
};

/**
 * Navigate to market page with trade highlighting
 * @param {Function} navigate - React Router navigate function
 * @param {Object} trade - Trade object
 * @param {string|number} tradeIndex - Trade index to highlight (optional)
 */
export const navigateToTradeMarket = (navigate, trade, tradeIndex = null) => {
  const route = getMarketRouteFromTrade(trade);
  if (!route) {
    console.error('Cannot navigate: market route not found for trade', trade);
    return;
  }

  // Extract trade index from user_trade_index if not provided
  let highlightIndex = tradeIndex;
  if (!highlightIndex && trade.user_trade_index) {
    // Extract number from "UserTradeIndex(123)" or just use the value
    const match = trade.user_trade_index.toString().match(/\((\d+)\)/);
    if (match) {
      highlightIndex = match[1];
    } else {
      highlightIndex = trade.user_trade_index;
    }
  }

  // Build URL with highlight parameter and auto-open detail modal
  const url = highlightIndex
    ? `${route}?highlightTrade=${highlightIndex}&openDetail=true`
    : route;

  navigate(url);
};

/**
 * Get trade index number from user_trade_index
 * @param {string|number} userTradeIndex - User trade index in various formats
 * @returns {string|null} Trade index number as string or null
 */
export const getTradeIndexNumber = (userTradeIndex) => {
  if (!userTradeIndex) return null;

  const match = userTradeIndex.toString().match(/\((\d+)\)/);
  if (match && match[1]) {
    return match[1];
  }

  const directNumber = parseInt(userTradeIndex, 10);
  return isNaN(directNumber) ? null : directNumber.toString();
};

